import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageMeta } from "@/components/common/PageMeta";
import { EvidenceBadge } from "@/components/common/EvidenceBadge";

function Block({ title, children }) {
  return (
    <div className="border-t border-[var(--line)] py-6">
      <p className="eyebrow mb-3">{title}</p>
      {children}
    </div>
  );
}

export default function CapabilityDetail() {
  const { slug } = useParams();
  const [cap, setCap] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setCap(null);
    setError(false);
    api.get(`/capabilities/${slug}`).then(({ data }) => setCap(data)).catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <div className="container-dbrf py-32 text-center">
        <p className="font-display text-2xl text-navy">Capability not found.</p>
        <Link to="/capabilities" className="mt-4 inline-block text-[var(--cobalt)] link-underline">Back to catalogue</Link>
      </div>
    );

  if (!cap) return <div className="container-dbrf py-32 text-center font-mono text-sm text-[var(--mineral)]">Loading…</div>;

  return (
    <>
      <PageMeta title={cap.title} description={cap.summary} />
      <div className="border-b border-[var(--line)] bg-[var(--paper)]">
        <div className="container-dbrf py-12 lg:py-16">
          <Link to="/capabilities" data-testid="back-to-catalogue" className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--cobalt)]">
            <ArrowLeft size={15} /> Catalogue
          </Link>
          <nav aria-label="Breadcrumb" className="mb-4 font-mono text-xs text-[var(--mineral)]">
            Capabilities / {cap.domain}
          </nav>
          <div className="flex flex-wrap items-center gap-3">
            <EvidenceBadge status={cap.evidence_status} raw={cap.evidence_status} />
            <span className="rounded-full border border-[var(--line)] px-2.5 py-1 font-mono text-xs text-[var(--mineral)]">{cap.readiness}</span>
            <span className="rounded-full border border-[var(--line)] px-2.5 py-1 font-mono text-xs text-[var(--mineral)]">Access: {cap.access_status}</span>
          </div>
          <h1 className="mt-4 max-w-3xl font-display font-extrabold text-navy" style={{ fontSize: "clamp(2rem,4vw,3.4rem)", lineHeight: 1.04 }}>
            {cap.title}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-[var(--mineral)]">{cap.summary}</p>
        </div>
      </div>

      <div className="container-dbrf grid gap-12 py-14 lg:grid-cols-[1fr_320px]">
        <div className="reading">
          <Block title="Scientific context"><p className="text-[var(--mineral)]">{cap.scientific_context}</p></Block>
          <Block title="Methods">
            <div className="flex flex-wrap gap-2">
              {cap.methods.map((m) => <span key={m} className="rounded-full border border-[var(--line)] bg-[var(--ivory)] px-3 py-1 font-mono text-xs text-navy">{m}</span>)}
            </div>
          </Block>
          <Block title="Related disciplines">
            <div className="flex flex-wrap gap-2">
              {cap.disciplines.map((d) => (
                <Link key={d} to={`/capabilities?discipline=${encodeURIComponent(d)}`} className="rounded-full border border-[var(--line)] px-3 py-1 text-sm text-navy hover:border-[var(--cobalt)] hover:text-[var(--cobalt)]">{d}</Link>
              ))}
            </div>
          </Block>
          <Block title="Problems it may address">
            <ul className="space-y-2 text-[var(--mineral)]">
              {cap.problems_addressed.map((p) => <li key={p} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--amber)]" />{p}</li>)}
            </ul>
          </Block>
          <Block title="Typical work packages">
            <ol className="space-y-3">
              {cap.work_packages.map((w, i) => (
                <li key={w} className="flex gap-3"><span className="font-mono text-sm text-[var(--cobalt)]">{String(i + 1).padStart(2, "0")}</span><span className="text-[var(--mineral)]">{w}</span></li>
              ))}
            </ol>
          </Block>
          <Block title="Potential outputs">
            <div className="flex flex-wrap gap-2">
              {cap.output_types.map((o) => <span key={o} className="rounded-full bg-[var(--cobalt)]/10 px-3 py-1 text-sm text-[var(--cobalt)]">{o}</span>)}
            </div>
          </Block>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-[var(--line)] bg-[var(--navy)] p-7 text-[var(--ivory)]">
            <p className="font-display text-lg font-bold">Network expertise required</p>
            <p className="mt-2 text-sm text-[var(--ivory)]/70">DBRF assembles the relevant specialists from its professional network for each engagement.</p>
            <Link to="/bring-a-challenge" data-testid="capability-cta" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--amber)] px-5 py-3 text-sm font-semibold text-[var(--navy)]">
              Discuss this capability <ArrowRight size={15} />
            </Link>
          </div>
          {cap.related_capabilities?.length > 0 && (
            <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6">
              <p className="eyebrow mb-3">Related capabilities</p>
              <ul className="space-y-2">
                {cap.related_capabilities.map((r) => (
                  <li key={r.slug}><Link to={`/capabilities/${r.slug}`} className="text-sm text-navy hover:text-[var(--cobalt)] link-underline">{r.title}</Link></li>
                ))}
              </ul>
            </div>
          )}
          {cap.related_projects?.length > 0 && (
            <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6">
              <p className="eyebrow mb-3">Related projects</p>
              <ul className="space-y-2">
                {cap.related_projects.map((r) => (
                  <li key={r.slug}><Link to={`/projects/${r.slug}`} className="text-sm text-navy hover:text-[var(--cobalt)] link-underline">{r.title}</Link></li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </>
  );
}
